import React, { useEffect, useRef, useState } from 'react';
import { Button, Input } from '@heroui/react';
import { Select, SelectItem } from '@/components/ui/AppSelect';
import { Drawer, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter } from '@heroui/drawer';
import * as XLSX from 'xlsx';
import { createBulkFreeOrders } from '../apiEndpoints';
import useIsMobile from '../hooks/useIsMobile';
import { currencySymbol } from '@/utils/currency';
import { btnAccent, btnGhost, label as labelCls } from '@/components/ui/design';

const fieldWrap = 'rounded-2xl bg-white shadow-none ring-1 ring-black/[0.06] data-[hover=true]:bg-white group-data-[focus=true]:bg-white group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60';
const fieldText = 'text-sm text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted';
const inputCls = { inputWrapper: `h-14 ${fieldWrap}`, input: fieldText };
const qtyInputCls = { inputWrapper: `h-14 ${fieldWrap}`, input: `${fieldText} text-center` };
const selectCls = { trigger: fieldWrap, value: fieldText };

export interface BulkTicketPromoCode {
    _id?: string;
    codeName: string;
    discount: string;
    discountType: 'fixed' | 'percentage';
    isActive: boolean;
    startDate: string;
    endDate: string;
}

export interface BulkTicket {
    _id: string;
    name: string;
    remained: number;
    type: string;
    price?: number;
    promocodes?: BulkTicketPromoCode[];
}

export interface EventTime {
    startDate: string;
    endDate?: string;
}

interface Row {
    id: number;
    name: string;
    email: string;
    phone: string;
    ticketId: string;
    qty: number;
    // set after submission
    status?: 'queued' | 'failed';
    failReason?: string;
}

interface BulkFreeTicketsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    eventId: string;
    tickets: BulkTicket[];
    eventTimes?: EventTime[];
    currency?: string;
}

let nextId = 1;
function makeRow(overrides: Partial<Row> = {}): Row {
    return { id: nextId++, name: '', email: '', phone: '', ticketId: '', qty: 1, ...overrides };
}

export function rowsFromSheetData(data: string[][], tickets: BulkTicket[]): Row[] {
    if (data.length < 2) throw new Error('File must have a header row and at least one data row');

    const headers = data[0].map(h => String(h ?? '').trim().toLowerCase());
    const nameIdx = headers.findIndex(h => h.includes('name') && !h.includes('ticket'));
    const emailIdx = headers.findIndex(h => h.includes('email'));
    const phoneIdx = headers.findIndex(h => h.includes('phone') || h.includes('mobile') || h.includes('cell') || h.includes('tel'));
    const ticketIdx = headers.findIndex(h => h.includes('ticket'));
    const qtyIdx = headers.findIndex(h => h.includes('qty') || h.includes('quantity') || h.includes('count') || h === 'q');

    if (nameIdx === -1 || emailIdx === -1) {
        throw new Error('File must have columns containing "name" and "email"');
    }

    return data.slice(1).filter(cols => cols.some(c => String(c ?? '').trim())).map((cols, i) => {
        const name = String(cols[nameIdx] ?? '').trim();
        const email = String(cols[emailIdx] ?? '').trim();
        if (!name || !email) throw new Error(`Row ${i + 2}: name and email are required`);

        let ticketId = '';
        if (ticketIdx !== -1 && cols[ticketIdx]) {
            const match = tickets.find(t =>
                t.name.toLowerCase() === String(cols[ticketIdx]).trim().toLowerCase()
            );
            if (match) ticketId = match._id;
        }
        if (!ticketId && tickets.length === 1) ticketId = tickets[0]._id;

        const qtyRaw = qtyIdx !== -1 ? parseInt(String(cols[qtyIdx] ?? ''), 10) : NaN;
        const qty = !isNaN(qtyRaw) && qtyRaw >= 1 ? qtyRaw : 1;
        return makeRow({ name, email, phone: phoneIdx !== -1 ? String(cols[phoneIdx] ?? '').trim() : '', ticketId, qty });
    });
}

function parseFile(file: File, tickets: BulkTicket[]): Promise<Row[]> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        const isCsv = file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv';

        reader.onload = (ev) => {
            try {
                const raw = ev.target?.result;
                let data: string[][];
                if (isCsv) {
                    const lines = (raw as string).trim().split(/\r?\n/);
                    data = lines.map(l => l.split(',').map(c => c.replace(/^"|"$/g, '')));
                } else {
                    const wb = XLSX.read(raw, { type: 'array' });
                    const ws = wb.Sheets[wb.SheetNames[0]];
                    data = XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, defval: '' });
                }
                resolve(rowsFromSheetData(data, tickets));
            } catch (err: any) {
                reject(err);
            }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));

        if (isCsv) {
            reader.readAsText(file);
        } else {
            reader.readAsArrayBuffer(file);
        }
    });
}

export default function BulkFreeTicketsModal({ isOpen, onClose, onSuccess, eventId, tickets, eventTimes, currency }: BulkFreeTicketsModalProps) {
    const isMobile = useIsMobile();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [rows, setRows] = useState<Row[]>([makeRow()]);
    const [csvError, setCsvError] = useState('');
    const [inventoryError, setInventoryError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [bulkQty, setBulkQty] = useState(1);
    const [bulkTicketId, setBulkTicketId] = useState('');
    const [selectedDate, setSelectedDate] = useState(() => eventTimes?.[0]?.startDate ?? '');
    // promoCode per ticketId — only for paid tickets
    const [promoCodeByTicket, setPromoCodeByTicket] = useState<Record<string, string>>({});

    useEffect(() => {
        setSelectedDate(eventTimes?.[0]?.startDate ?? '');
    }, [eventTimes]);

    function applyBulkQty() {
        const next = rows.map(r => ({ ...r, qty: bulkQty }));
        setRows(next);
        checkInventory(next);
    }

    function applyBulkTicket() {
        if (!bulkTicketId) return;
        const next = rows.map(r => ({ ...r, ticketId: bulkTicketId }));
        setRows(next);
        checkInventory(next);
    }
    const sortedRows = [...rows].sort((a, b) =>
        a.email.toLowerCase().localeCompare(b.email.toLowerCase())
    );

    // ── inventory check ─────────────────────────────────────────────────────
    function checkInventory(nextRows: Row[]) {
        // Group by ticketId, check 20% buffer per ticket
        const countByTicket = new Map<string, number>();
        for (const r of nextRows) {
            if (r.ticketId) countByTicket.set(r.ticketId, (countByTicket.get(r.ticketId) || 0) + (r.qty || 1));
        }
        for (const [tid, count] of countByTicket) {
            const t = tickets.find(tk => tk._id === tid);
            if (!t) continue;
            if (t.remained < count * 1.2) {
                setInventoryError(
                    `"${t.name}": ${t.remained} remaining but need at least ${Math.ceil(count * 1.2)} (20% buffer over ${count} tickets)`
                );
                return;
            }
        }
        setInventoryError('');
    }

    // ── CSV upload ───────────────────────────────────────────────────────────
    async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
        setCsvError('');
        setInventoryError('');
        setSubmitted(false);
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const parsed = await parseFile(file, tickets);
            setRows(parsed);
            checkInventory(parsed);
        } catch (err: any) {
            setCsvError(err.message);
        }
    }

    // ── row mutations ────────────────────────────────────────────────────────
    function updateRow(id: number, field: keyof Row, value: string | number) {
        const next = rows.map(r => r.id === id ? { ...r, [field]: field === 'qty' ? Number(value) : value } : r);
        setRows(next);
        checkInventory(next);
        setSubmitted(false);
    }

    function addRow() {
        const next = [...rows, makeRow({ ticketId: rows[rows.length - 1]?.ticketId || '' })];
        setRows(next);
        checkInventory(next);
        setSubmitted(false);
    }

    function deleteRow(id: number) {
        const next = rows.filter(r => r.id !== id);
        setRows(next.length ? next : [makeRow()]);
        checkInventory(next);
        setSubmitted(false);
    }

    // ── validation ───────────────────────────────────────────────────────────
    function rowErrors(r: Row) {
        const errs: string[] = [];
        if (!r.name.trim()) errs.push('name required');
        if (!r.email.trim() || !/\S+@\S+\.\S+/.test(r.email)) errs.push('valid email required');
        if (!r.ticketId) errs.push('select a ticket');
        return errs;
    }

    const duplicateEmails = new Set(
        rows
            .map(r => r.email.trim().toLowerCase())
            .filter((email, _, arr) => email && arr.indexOf(email) !== arr.lastIndexOf(email))
    );

    const allValid = rows.every(r => rowErrors(r).length === 0);
    const canSubmit = allValid && duplicateEmails.size === 0 && !inventoryError && !isSubmitting && !submitted;

    // ── submit ───────────────────────────────────────────────────────────────
    async function handleSubmit() {
        if (!canSubmit) return;

        // Group rows by ticketId
        const byTicket = new Map<string, Row[]>();
        for (const r of rows) {
            const group = byTicket.get(r.ticketId) || [];
            group.push(r);
            byTicket.set(r.ticketId, group);
        }

        setIsSubmitting(true);
        const failMap = new Map<string, string>(); // email → reason

        for (const [ticketId, group] of byTicket) {
            try {
                const res = await createBulkFreeOrders({
                    eventId,
                    ticketId,
                    rows: group.map(r => ({ name: r.name, email: r.email, phone: r.phone || undefined, qty: r.qty || 1 })),
                    selectedDate: selectedDate || undefined,
                    promoCode: promoCodeByTicket[ticketId] || undefined,
                });
                for (const f of res.data.failed) failMap.set(f.email, f.reason);
            } catch (err: any) {
                const msg = err?.response?.data?.message || err.message || 'Request failed';
                for (const r of group) failMap.set(r.email, msg);
            }
        }

        setRows(prev => prev.map(r => ({
            ...r,
            status: failMap.has(r.email) ? 'failed' : 'queued',
            failReason: failMap.get(r.email),
        })));
        setIsSubmitting(false);
        setSubmitted(true);
        onSuccess?.();
    }

    // ── reset & close ────────────────────────────────────────────────────────
    function handleClose() {
        setRows([makeRow()]);
        setCsvError('');
        setInventoryError('');
        setIsSubmitting(false);
        setSubmitted(false);
        setSelectedDate(eventTimes?.[0]?.startDate ?? '');
        setPromoCodeByTicket({});
        if (fileInputRef.current) fileInputRef.current.value = '';
        onClose();
    }

    const queuedCount = rows.filter(r => r.status === 'queued').length;
    const failedCount = rows.filter(r => r.status === 'failed').length;

    const cardClass = 'rounded-[28px] bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] p-5';
    const sectionTitle = 'text-xs font-bold uppercase tracking-[0.18em] text-grey40 dark:text-nightMuted';
    const hint = 'text-sm text-grey40 dark:text-nightMuted';
    const linkBtn = 'inline-flex h-10 items-center justify-center rounded-full px-4 text-sm font-semibold text-grey10 ring-1 ring-grey90 hover:bg-white dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06] whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed transition';

    return (
        <Drawer
            placement="right"
            hideCloseButton
            isOpen={isOpen}
            onClose={handleClose}
            size={isMobile ? 'full' : '4xl'}
            scrollBehavior="inside"
            classNames={{
                wrapper: isMobile ? 'z-[100000]' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000]',
                base: isMobile ? 'bg-white dark:bg-nightSurface h-[100dvh] my-0 z-[100000]' : 'bg-white dark:bg-nightSurface h-[calc(100dvh-80px)] my-0 z-[100000] rounded-l-[28px] ring-1 ring-black/[0.06] dark:ring-white/10',
                backdrop: isMobile ? 'z-[100000] bg-black/50' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000] bg-black/50',
            }}
        >
            <DrawerContent>
                <DrawerHeader className="flex items-start justify-between px-6 pt-6 pb-4">
                    <div>
                        <h2 className="text-[28px] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">Add Guests</h2>
                        <p className={`${hint} font-normal mt-2`}>Send free tickets to one or more guests</p>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="w-10 h-10 flex items-center justify-center rounded-full ring-1 ring-grey90 dark:ring-white/15 text-grey10 dark:text-white hover:bg-grey99 dark:hover:bg-white/[0.06] transition-colors flex-shrink-0"
                        aria-label="Close"
                    >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    </button>
                </DrawerHeader>

                <DrawerBody className="px-6 py-2 gap-5">
                    {!submitted && (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-[28px] border border-dashed border-grey80 dark:border-white/15 p-5">
                            <div className="w-12 h-12 rounded-full bg-primary/10 dark:bg-nightAccent/15 text-primary dark:text-nightAccent flex items-center justify-center flex-shrink-0">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 16V4m0 0l-4 4m4-4l4 4M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-base font-bold tracking-[-0.02em] text-grey10 dark:text-white">Import from file</p>
                                <p className={`${hint} mt-1`}>
                                    CSV or XLSX with <code>name</code>, <code>email</code>, <code>phone</code> (optional), <code>ticket</code> (optional) columns
                                </p>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                                onChange={handleFileChange}
                                className="hidden"
                            />
                            <Button
                                onPress={() => fileInputRef.current?.click()}
                                className={btnAccent}
                            >
                                Upload File
                            </Button>
                        </div>
                    )}

                    {csvError && (
                        <div className="rounded-2xl bg-error95 dark:bg-error50/15 px-4 py-3 text-sm font-semibold text-error40 dark:text-error70">{csvError}</div>
                    )}

                    {inventoryError && (
                        <div className="rounded-2xl bg-error95 dark:bg-error50/15 px-4 py-3 text-sm font-semibold text-error40 dark:text-error70">{inventoryError}</div>
                    )}

                    {!submitted && duplicateEmails.size > 0 && (
                        <div className="rounded-2xl bg-amber-50 dark:bg-amber-500/10 px-4 py-3 text-sm font-semibold text-amber-800 dark:text-amber-300">
                            {duplicateEmails.size} duplicate email{duplicateEmails.size !== 1 ? 's' : ''} detected. Remove duplicates before sending.
                        </div>
                    )}

                    {submitted && (
                        <div className="flex items-center gap-2">
                            <span className="inline-flex items-center h-8 px-3.5 rounded-full text-sm font-semibold bg-success95 dark:bg-success50/15 text-success30 dark:text-success60">{queuedCount} queued</span>
                            {failedCount > 0 && (
                                <span className="inline-flex items-center h-8 px-3.5 rounded-full text-sm font-semibold bg-error95 dark:bg-error50/15 text-error40 dark:text-error70">{failedCount} failed</span>
                            )}
                        </div>
                    )}

                    {(() => {
                        const paidTicketsWithRows = tickets.filter(t =>
                            (t.price ?? 0) > 0 &&
                            (t.promocodes?.length ?? 0) > 0 &&
                            rows.some(r => r.ticketId === t._id)
                        );
                        const showDate = !!eventTimes && eventTimes.length > 1;
                        if (!showDate && paidTicketsWithRows.length === 0) return null;
                        return (
                            <div className={`${cardClass} flex flex-col gap-4`}>
                                <p className={sectionTitle}>Order settings</p>
                                {showDate && (
                                    <div>
                                        <label className={labelCls}>Event date</label>
                                        <Select
                                            aria-label="Event date"
                                            placeholder="Select a date..."
                                            selectedKeys={selectedDate ? [selectedDate] : []}
                                            onSelectionChange={keys => setSelectedDate((Array.from(keys)[0] as string) || '')}
                                            isDisabled={submitted}
                                            classNames={selectCls}
                                        >
                                            {eventTimes!.map(t => (
                                                <SelectItem key={t.startDate} textValue={t.startDate}>{t.startDate}</SelectItem>
                                            ))}
                                        </Select>
                                    </div>
                                )}
                                {paidTicketsWithRows.map(t => (
                                    <div key={t._id}>
                                        <label className={`${labelCls} truncate`} title={t.name}>Promo code for {t.name}</label>
                                        <Select
                                            aria-label={`Promo code for ${t.name}`}
                                            placeholder="No promo code"
                                            selectedKeys={promoCodeByTicket[t._id] ? [promoCodeByTicket[t._id]] : []}
                                            onSelectionChange={keys => setPromoCodeByTicket(prev => ({ ...prev, [t._id]: (Array.from(keys)[0] as string) || '' }))}
                                            isDisabled={submitted}
                                            classNames={selectCls}
                                        >
                                            {(t.promocodes ?? []).filter(p => p.isActive).map(p => (
                                                <SelectItem key={p.codeName} textValue={p.codeName}>
                                                    {p.codeName} ({p.discountType === 'percentage' ? `${p.discount}%` : `${currencySymbol(currency)}${p.discount}`} off)
                                                </SelectItem>
                                            ))}
                                        </Select>
                                    </div>
                                ))}
                            </div>
                        );
                    })()}

                    {!submitted && (
                        <div className={`${cardClass} flex flex-col gap-3`}>
                            <p className={sectionTitle}>Apply to all guests</p>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <div className="flex items-center gap-3 flex-1">
                                    <Select
                                        aria-label="Ticket for all guests"
                                        placeholder="Select ticket..."
                                        selectedKeys={bulkTicketId ? [bulkTicketId] : []}
                                        onSelectionChange={keys => setBulkTicketId((Array.from(keys)[0] as string) || '')}
                                        className="flex-1"
                                        classNames={selectCls}
                                    >
                                        {tickets.map(t => (
                                            <SelectItem key={t._id} textValue={t.name}>{t.name}</SelectItem>
                                        ))}
                                    </Select>
                                    <button onClick={applyBulkTicket} disabled={!bulkTicketId} className={linkBtn}>Apply</button>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Input
                                        aria-label="Quantity for all guests"
                                        type="number"
                                        min={1}
                                        value={String(bulkQty)}
                                        onChange={e => {
                                            const v = parseInt(e.target.value, 10);
                                            setBulkQty(!isNaN(v) && v >= 1 ? v : 1);
                                        }}
                                        className="w-24"
                                        classNames={qtyInputCls}
                                    />
                                    <button onClick={applyBulkQty} className={linkBtn}>Apply qty</button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                            <p className={sectionTitle}>Guests</p>
                            <span className={hint}>{rows.length} guest{rows.length !== 1 ? 's' : ''}</span>
                        </div>

                        <div className="hidden md:grid grid-cols-[32px_1fr_1.2fr_1fr_1fr_88px_32px] gap-3 px-5 text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted">
                            <span>#</span>
                            <span>Name</span>
                            <span>Email</span>
                            <span>Phone</span>
                            <span>Ticket</span>
                            <span>Qty</span>
                            <span />
                        </div>

                        {sortedRows.map((row, idx) => {
                            const rowFailed = row.status === 'failed';
                            const rowQueued = row.status === 'queued';
                            const isDuplicate = !submitted && duplicateEmails.has(row.email.trim().toLowerCase());
                            const stateClass = rowFailed
                                ? 'ring-1 ring-error50/40 bg-error95 dark:bg-error50/10'
                                : rowQueued
                                    ? 'ring-1 ring-success40/40 bg-success95 dark:bg-success50/10'
                                    : isDuplicate
                                        ? 'ring-1 ring-amber-500/50 bg-amber-50 dark:bg-amber-500/10'
                                        : 'bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]';
                            return (
                                <div key={row.id} className={`rounded-2xl p-4 ${stateClass}`}>
                                    <div className="grid grid-cols-1 md:grid-cols-[32px_1fr_1.2fr_1fr_1fr_88px_32px] gap-3 md:items-center">
                                        <div className="flex items-center justify-between md:block">
                                            <span className="inline-flex h-7 min-w-7 items-center justify-center rounded-full bg-white px-2 text-xs font-bold text-grey30 ring-1 ring-black/[0.06] dark:bg-white/10 dark:text-white dark:ring-0">{idx + 1}</span>
                                            <div className="md:hidden">
                                                {!submitted && (
                                                    <button onClick={() => deleteRow(row.id)} className="flex h-8 w-8 items-center justify-center rounded-full bg-error95 text-error50 hover:bg-error90 dark:bg-error50/15 dark:text-error60 dark:hover:bg-error50/25 transition" title="Remove guest">
                                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <Input
                                            aria-label="Name"
                                            placeholder="Name..."
                                            value={row.name}
                                            onChange={e => updateRow(row.id, 'name', e.target.value)}
                                            isDisabled={submitted}
                                            isInvalid={!submitted && !row.name.trim()}
                                            classNames={inputCls}
                                        />
                                        <Input
                                            aria-label="Email"
                                            placeholder="Email..."
                                            value={row.email}
                                            onChange={e => updateRow(row.id, 'email', e.target.value)}
                                            isDisabled={submitted}
                                            isInvalid={!submitted && ((!row.email || !/\S+@\S+\.\S+/.test(row.email)) || isDuplicate)}
                                            classNames={inputCls}
                                        />
                                        <Input
                                            aria-label="Phone"
                                            placeholder="Phone..."
                                            value={row.phone}
                                            onChange={e => updateRow(row.id, 'phone', e.target.value)}
                                            isDisabled={submitted}
                                            classNames={inputCls}
                                        />
                                        <Select
                                            aria-label="Ticket"
                                            placeholder="Ticket..."
                                            selectedKeys={row.ticketId ? [row.ticketId] : []}
                                            onSelectionChange={keys => {
                                                const v = Array.from(keys)[0] as string;
                                                updateRow(row.id, 'ticketId', v || '');
                                            }}
                                            isDisabled={submitted}
                                            isInvalid={!submitted && !row.ticketId}
                                            classNames={selectCls}
                                        >
                                            {tickets.map(t => (
                                                <SelectItem key={t._id} textValue={t.name}>
                                                    <span className="text-sm">{t.name}</span>
                                                    <span className="text-xs text-default-400 ml-1">({t.remained} left)</span>
                                                </SelectItem>
                                            ))}
                                        </Select>
                                        <Input
                                            aria-label="Quantity"
                                            type="number"
                                            min={1}
                                            value={String(row.qty)}
                                            onChange={e => {
                                                const v = parseInt(e.target.value, 10);
                                                updateRow(row.id, 'qty', !isNaN(v) && v >= 1 ? v : 1);
                                            }}
                                            isDisabled={submitted}
                                            classNames={qtyInputCls}
                                        />
                                        <div className="hidden md:flex justify-center">
                                            {!submitted && (
                                                <button onClick={() => deleteRow(row.id)} className="flex h-8 w-8 items-center justify-center rounded-full bg-error95 text-error50 hover:bg-error90 dark:bg-error50/15 dark:text-error60 dark:hover:bg-error50/25 transition" title="Remove guest">
                                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                                                </button>
                                            )}
                                            {rowQueued && (
                                                <svg width="16" height="16" viewBox="0 0 14 14" fill="none" className="text-success40 dark:text-success60">
                                                    <path d="M2 7l3.5 3.5L12 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            )}
                                        </div>
                                    </div>
                                    {rowFailed && (
                                        <p className="text-sm font-semibold text-error40 dark:text-error70 mt-2 md:pl-[44px]">{row.failReason}</p>
                                    )}
                                </div>
                            );
                        })}

                        {!submitted && (
                            <button
                                onClick={addRow}
                                className="flex items-center justify-center gap-2 h-12 rounded-full border border-dashed border-grey80 dark:border-white/15 text-sm font-semibold text-grey10 dark:text-white hover:bg-grey99 dark:hover:bg-white/[0.06] transition"
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                                Add Guest
                            </button>
                        )}
                    </div>
                </DrawerBody>

                <DrawerFooter className="px-6 pt-4 pb-6 border-t border-black/[0.06] dark:border-white/[0.06]">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 w-full">
                        <span className={hint}>
                            {rows.reduce((sum, r) => sum + (r.qty || 0), 0)} ticket{rows.reduce((sum, r) => sum + (r.qty || 0), 0) !== 1 ? 's' : ''} for {rows.length} guest{rows.length !== 1 ? 's' : ''}
                        </span>
                        <div className="flex gap-2">
                            <Button variant="light" onPress={handleClose} className={`${btnGhost} flex-1 sm:flex-none bg-transparent`}>Close</Button>
                            {!submitted ? (
                                <Button
                                    onPress={handleSubmit}
                                    isDisabled={!canSubmit}
                                    isLoading={isSubmitting}
                                    className={`${btnAccent} flex-1 sm:flex-none`}
                                >
                                    Send Tickets ({rows.length})
                                </Button>
                            ) : (
                                <Button onPress={handleClose} className={`${btnAccent} flex-1 sm:flex-none`}>Done</Button>
                            )}
                        </div>
                    </div>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
}
